import { PurchaseOrder } from "../types/purchaseOrder";
import {
  Order,
  Order as SalesOrder,
  type OrderItem
} from "../types/salesOrder";
import { OrderStatus } from "../enums/orderStatus";
import { PaymentStatus } from "../../Payment/enums/paymentStatus";
import { InvoiceStatus } from "../../Invoice/enums/invoice";
import { Invoice } from "../../Invoice/types/invoice";

// Orders are editable only when they have NOT reached a paid/terminal state
const UNEDITABLE_STATUSES: OrderStatus[] = [
  OrderStatus.PAID,
  OrderStatus.COMPLETED,
  OrderStatus.CANCELLED
];

/**
 * Format currency amount
 */
export const formatCurrency = (
  amount: number,
  currency: string = "USD"
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(amount);
};

/**
 * Format date string
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

/**
 * Format date and time string
 */
export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

/**
 * Get status color for order status (for Chip component)
 */
export const getStatusColor = (
  status: OrderStatus
): "default" | "info" | "success" | "warning" | "error" => {
  switch (status) {
    case OrderStatus.DRAFT:
      return "default";
    case OrderStatus.OFFER_SUGGESTED:
      return "info";
    case OrderStatus.OFFER_ACCEPTED:
    case OrderStatus.PAID:
    case OrderStatus.COMPLETED:
      return "success";
    case OrderStatus.PENDING_PAYMENT:
      return "warning";
    case OrderStatus.CANCELLED:
      return "error";
    default:
      return "default";
  }
};

/**
 * Get status color for order status (comprehensive version for Chip)
 */
export const getOrderStatusColor = (
  status?: OrderStatus
): "default" | "success" | "warning" | "error" => {
  if (!status) return "default";
  switch (status) {
    case OrderStatus.COMPLETED:
    case OrderStatus.OFFER_ACCEPTED:
    case OrderStatus.PAID:
      return "success";
    case OrderStatus.CANCELLED:
      return "error";
    case OrderStatus.DRAFT:
    case OrderStatus.OFFER_SUGGESTED:
    case OrderStatus.PENDING_PAYMENT:
      return "warning";
    default:
      return "default";
  }
};

/**
 * Get status color for order status (for Typography color prop - CSS color strings)
 */
export const getOrderStatusTextColor = (status: OrderStatus): string => {
  switch (status) {
    case OrderStatus.DRAFT:
      return "gray";
    case OrderStatus.OFFER_SUGGESTED:
      return "blue";
    case OrderStatus.OFFER_ACCEPTED:
      return "green";
    case OrderStatus.CANCELLED:
      return "red";
    case OrderStatus.COMPLETED:
      return "darkgreen";
    case OrderStatus.PENDING_PAYMENT:
      return "orange";
    case OrderStatus.PAID:
      return "green";
    default:
      return "gray";
  }
};

/**
 * Get status color for payment/invoice status.
 * Handles both the legacy PaymentStatus enum (PARTIAL) and the
 * InvoiceStatus enum (PARTIAL_PAID, OVERPAID) so that InvoiceList
 * and InvoiceDetail both render the correct chip colors.
 */
export const getPaymentStatusColor = (
  status: PaymentStatus | InvoiceStatus | string
): "default" | "success" | "warning" | "error" | "info" => {
  switch (status) {
    case PaymentStatus.PAID:
    case InvoiceStatus.PAID:
      return "success";
    case PaymentStatus.PARTIAL:
    case InvoiceStatus.PARTIAL_PAID:
      return "warning";
    case PaymentStatus.UNPAID:
    case InvoiceStatus.UNPAID:
      return "error";
    case InvoiceStatus.OVERPAID:
      return "info";
    default:
      return "default";
  }
};

/**
 * Calculate order total from line items
 */
export const calculateOrderTotal = (
  lineItems: PurchaseOrder["lineItems"] | SalesOrder["lineItems"],
  tax?: number,
  shippingCost?: number
): number => {
  const subtotal = lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
  return subtotal + (tax || 0) + (shippingCost || 0);
};

/**
 * Generate document number (placeholder - should be done by backend)
 */
export const generateDocumentNumber = (
  prefix: string,
  sequence: number
): string => {
  return `${prefix}-${String(sequence).padStart(3, "0")}`;
};

/**
 * Check if order can be edited
 */
export const canEditOrder = (status: OrderStatus): boolean => {
  return status === OrderStatus.DRAFT || status === OrderStatus.OFFER_SUGGESTED;
};

/**
 * Check if order can be cancelled (UI: show until terminal completed; hide when already cancelled).
 */
export const canCancelOrder = (status: OrderStatus): boolean => {
  return status !== OrderStatus.COMPLETED && status !== OrderStatus.CANCELLED;
};

/**
 * Confirm order (move to OFFER_ACCEPTED) before issuing an invoice.
 */
export const canConfirmOrder = (order: Order): boolean => {
  const hasItems =
    Array.isArray(order.items) &&
    order.items.length > 0 &&
    order.items.some((item) => item.quantity > 0 && item.unitPrice > 0);

  return (
    (order.status === OrderStatus.DRAFT ||
      order.status === OrderStatus.OFFER_SUGGESTED) &&
    hasItems
  );
};

/**
 * Complete order in OMS (inventory COMPLETE_SALE) — only after the order is fully paid.
 */
export const canCompleteOrder = (status: OrderStatus): boolean => {
  return status === OrderStatus.PAID;
};

/**
 * Derive the linked invoice for a given order.
 * Returns the invoice if its orderId matches, otherwise null.
 */
export const getLinkedInvoice = (
  invoiceDetail: Invoice | null | undefined,
  orderId: string | undefined
): Invoice | null => {
  return invoiceDetail?.orderId === orderId ? invoiceDetail : null;
};

/**
 * Check if an invoice can be issued for this order.
 * Requires: no existing invoice, status is OFFER_ACCEPTED, and the order
 * has at least one item with quantity > 0 and a non-zero unit price.
 */
export const canIssueInvoice = (
  linkedInvoice: Invoice | null,
  orderStatus?: OrderStatus,
  order?: Order | null
): boolean => {
  if (linkedInvoice || orderStatus !== OrderStatus.OFFER_ACCEPTED) return false;
  if (!order) return false;
  return (
    Array.isArray(order.items) &&
    order.items.length > 0 &&
    order.items.some((item) => item.quantity > 0 && item.unitPrice > 0)
  );
};

/**
 * Check if a payment can be recorded against the linked invoice.
 * Pass the already-resolved linkedInvoice (null if none).
 */
export const canRecordPayment = (
  linkedInvoice: Invoice | null | undefined
): boolean => {
  return (
    !!linkedInvoice &&
    (linkedInvoice.status === InvoiceStatus.UNPAID ||
      linkedInvoice.status === InvoiceStatus.PARTIAL_PAID)
  );
};

/** Detect opaque UUID-style ids (hide in UI; optional tooltip for support). */
export const isLikelyUuid = (value: string): boolean => {
  const s = value.trim();
  return (
    s.length >= 32 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
  );
};

/** English defaults for `order-status-*` i18n keys (sales order {@link OrderStatus}). */
export const ORDER_STATUS_LABEL_DEFAULTS: Record<OrderStatus, string> = {
  [OrderStatus.DRAFT]: "Draft",
  [OrderStatus.OFFER_SUGGESTED]: "Offer suggested",
  [OrderStatus.OFFER_ACCEPTED]: "Offer accepted",
  [OrderStatus.PENDING_PAYMENT]: "Pending payment",
  [OrderStatus.PAID]: "Paid",
  [OrderStatus.COMPLETED]: "Completed",
  [OrderStatus.CANCELLED]: "Cancelled"
};

function orderStatusTranslationKey(status: string): string {
  return `order-status-${status.toLowerCase().replace(/_/g, "-")}`;
}

function fallbackLabelForUnknownOrderStatus(status: string): string {
  return status
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Localized label for a sales order status. API values stay enum strings; UI uses i18n.
 */
export function formatOrderStatusLabel(
  status: OrderStatus | string | undefined | null,
  t: (key: string, defaultValue: string) => string
): string {
  const raw = status == null || status === "" ? "" : String(status).trim();
  if (!raw) return "";
  const known = ORDER_STATUS_LABEL_DEFAULTS[raw as OrderStatus];
  const defaultText = known ?? fallbackLabelForUnknownOrderStatus(raw);
  return t(orderStatusTranslationKey(raw), defaultText);
}

export const isOrderEditable = (status: OrderStatus): boolean =>
  !UNEDITABLE_STATUSES.includes(status);

/**
 * Primary label for a sales order line: persisted/top-level variation, then detail fields,
 * then an optional name from inventory fetch (e.g. edit modal), then item id.
 */
export function orderItemLineLabel(
  item: OrderItem,
  fetchedProductName?: string | null
): string {
  const fromOrder =
    item.variationName?.trim() ||
    item.detail?.variationName?.trim() ||
    item.detail?.productName?.trim() ||
    "";
  if (fromOrder) return fromOrder;
  const fetched = fetchedProductName?.trim();
  if (fetched) return fetched;
  return item.itemId;
}
